import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Building,
  MapPin,
  Clock,
  DollarSign,
  Calendar,
  Users,
  Briefcase,
  ExternalLink,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [selectedResume, setSelectedResume] = useState('');
  const [coverLetter, setCoverLetter] = useState('');

  useEffect(() => {
    fetchJob();
    fetchResumes();
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await api.get(`/api/jobs/${id}/`);
      setJob(response.data);
    } catch (error) {
      toast.error('Failed to fetch job details');
      console.error('Error fetching job:', error);
      navigate('/candidate-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchResumes = async () => {
    try {
      const response = await api.get('/api/profile/resumes/');
      const resumesData = Array.isArray(response.data) ? response.data : response.data?.results || [];
      setResumes(resumesData);
      // Set primary resume as default selection
      const primaryResume = resumesData.find(r => r.is_primary) || resumesData[0];
      if (primaryResume) {
        setSelectedResume(primaryResume.id.toString());
      }
    } catch (error) {
      console.error('Error fetching resumes:', error);
      setResumes([]);
    }
  };

  const handleApply = async () => {
    if (!selectedResume) {
      toast.error('Please select a resume');
      return;
    }

    setApplying(true);
    try {
      console.log('Applying to job ID:', id);
      console.log('User ID:', user.id);
      console.log('Selected resume ID:', selectedResume);
      
      const applicationData = {
        job: parseInt(id),
        candidate: user.id,
        resume: parseInt(selectedResume),
        cover_letter: coverLetter || 'I am interested in this position and would like to apply.'
      };
      
      console.log('Application data:', applicationData);
      
      await api.post('/api/applications/', applicationData);

      toast.success('Application submitted successfully!');
      navigate('/candidate-dashboard');
    } catch (error) {
      toast.error('Failed to submit application');
      console.error('Error applying to job:', error);
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Job not found</h3>
          <p className="mt-1 text-sm text-gray-500">The job you're looking for doesn't exist or has been removed.</p>
          <div className="mt-6">
            <Link
              to="/candidate-dashboard"
              className="btn-primary"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center space-x-4 mb-4">
              <button
                onClick={() => navigate('/candidate-dashboard')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Job Details</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-3 space-y-6">
            {/* Job Header */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="text-left">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2 text-left">{job.title}</h1>
                  <div className="flex items-center space-x-4 text-gray-600">
                    <div className="flex items-center">
                      <Building className="w-4 h-4 mr-2" />
                      {job.company_name}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {job.location_name}
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {job.job_type}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2" />
                  {job.job_type_name}
                </div>
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  Posted {new Date(job.created_at).toLocaleDateString()}
                </div>
                {job.salary_min && (
                  <div className="flex items-center text-gray-600">
                    <DollarSign className="w-4 h-4 mr-2" />
                    ${job.salary_min.toLocaleString()}
                    {job.salary_max && ` - $${job.salary_max.toLocaleString()}`}
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <Briefcase className="w-4 h-4 mr-2" />
                  {job.category_name}
                </div>
              </div>
            </div>

            {/* Job Description */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 text-left">Job Description</h2>
              <div className="prose max-w-none text-left">
                <p className="text-gray-700 whitespace-pre-wrap text-left">{job.description}</p>
              </div>
            </div>

            {/* Requirements */}
            {job.requirements && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 text-left">Requirements</h2>
                <div className="prose max-w-none text-left">
                  <p className="text-gray-700 whitespace-pre-wrap text-left">{job.requirements}</p>
                </div>
              </div>
            )}

            {/* Benefits */}
            {job.benefits && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 text-left">Benefits</h2>
                <div className="prose max-w-none text-left">
                  <p className="text-gray-700 whitespace-pre-wrap text-left">{job.benefits}</p>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1 space-y-6">
            {/* Apply Section */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-left">Apply for this job</h3>
              
              {resumes.length === 0 ? (
                <div className="text-left py-4">
                  <AlertCircle className="h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-4">You need to upload a resume before applying to jobs.</p>
                  <Link
                    to="/profile/resume"
                    className="btn-primary"
                  >
                    Upload Resume
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Resume
                    </label>
                    <select
                      value={selectedResume}
                      onChange={(e) => setSelectedResume(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {resumes.map((resume) => (
                        <option key={resume.id} value={resume.id}>
                          {resume.name} {resume.is_primary && '(Primary)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cover Letter (Optional)
                    </label>
                    <textarea
                      value={coverLetter}
                      onChange={(e) => setCoverLetter(e.target.value)}
                      rows={4}
                      placeholder="Write a cover letter explaining why you're interested in this position..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>

                  <button
                    onClick={handleApply}
                    disabled={applying || !selectedResume}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {applying ? 'Applying...' : 'Apply Now'}
                  </button>
                </div>
              )}
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 text-left">About {job.company_name}</h3>
              <div className="space-y-3">
                <div className="flex items-center text-sm text-gray-600">
                  <Building className="w-4 h-4 mr-2" />
                  {job.company_name}
                </div>
                {job.company_website && (
                  <div className="flex items-center text-sm text-gray-600">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    <a
                      href={job.company_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-500"
                    >
                      Company Website
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
